import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate } from 'react-router'
import { z } from 'zod'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { describeError } from '@/components/ui/error-copy'
import { AuthHero } from '@/features/auth/AuthHero'
import { completeRecoveryCodeLogin, completeTotpLogin } from '@/features/auth/api'
import { useAuth } from '@/features/auth/auth-context'
import { clearPreAuth, preAuthExpired, readPreAuth } from '@/features/auth/pre-auth'
import { ApiError } from '@/lib/api/problem'

export function TwoFactorChallengePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { completeChallenge } = useAuth()
  const challenge = readPreAuth()
  const [useRecovery, setUseRecovery] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const schema = useMemo(
    () =>
      useRecovery
        ? z.object({
            secret: z
              .string()
              .trim()
              .min(1, { message: t('auth.errors.recoveryRequired') }),
          })
        : z.object({
            secret: z
              .string()
              .trim()
              .regex(/^\d{6}$/u, { message: t('auth.errors.codeFormat') }),
          }),
    [t, useRecovery],
  )

  const form = useForm<{ secret: string }>({
    resolver: zodResolver(schema),
    defaultValues: { secret: '' },
  })

  if (challenge === null) {
    return <Navigate to="/login" replace />
  }

  const expired = preAuthExpired(challenge)

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null)

    try {
      const response = useRecovery
        ? await completeRecoveryCodeLogin(challenge.token, values.secret)
        : await completeTotpLogin(challenge.token, values.secret)

      if (response.tokens === undefined) {
        setFormError(t('auth.twoFactor.invalid'))
        return
      }

      clearPreAuth()
      completeChallenge(response.tokens)
      await navigate('/', { replace: true })
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setFormError(
          useRecovery ? t('auth.twoFactor.recoveryInvalid') : t('auth.twoFactor.invalid'),
        )
        form.setValue('secret', '')
        return
      }

      setFormError(t(describeError(error).description))
    }
  })

  const restart = (
    <Button
      variant="ghost"
      onClick={() => {
        clearPreAuth()
        void navigate('/login', { replace: true })
      }}
    >
      {t('auth.twoFactor.restart')}
    </Button>
  )

  if (expired) {
    return (
      <AuthHero compact title={t('auth.twoFactor.title')}>
        <p role="alert" className="text-sm text-error-strong">
          {t('auth.twoFactor.expired')}
        </p>
        {restart}
      </AuthHero>
    )
  }

  return (
    <AuthHero compact title={t('auth.twoFactor.title')} subtitle={t('auth.twoFactor.subtitle')}>
      <form noValidate onSubmit={(event) => void onSubmit(event)} className="flex flex-col gap-4">
        <Input
          key={useRecovery ? 'recovery' : 'code'}
          label={useRecovery ? t('auth.twoFactor.recoveryCode') : t('auth.twoFactor.code')}
          inputMode={useRecovery ? 'text' : 'numeric'}
          autoComplete="one-time-code"
          autoFocus
          required
          error={form.formState.errors.secret?.message}
          {...form.register('secret')}
        />

        {formError === null ? null : (
          <p role="alert" className="text-sm text-error-strong">
            {formError}
          </p>
        )}

        <Button type="submit" loading={form.formState.isSubmitting}>
          {form.formState.isSubmitting
            ? t('auth.twoFactor.submitting')
            : t('auth.twoFactor.submit')}
        </Button>

        <div className="flex flex-wrap justify-between gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setUseRecovery((current) => !current)
              setFormError(null)
              form.reset({ secret: '' })
            }}
          >
            {useRecovery ? t('auth.twoFactor.useCode') : t('auth.twoFactor.useRecovery')}
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              clearPreAuth()
              void navigate('/login', { replace: true })
            }}
          >
            {t('auth.twoFactor.restart')}
          </Button>
        </div>
      </form>
    </AuthHero>
  )
}
